/* eslint-disable no-loop-func */
import React, { useEffect, useRef, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { api, get } from "../../../utils/api";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import DeleteDialog from "../../../Components/Masters/DeleteDialog";
import MessagePopup from "../../../Components/Masters/MessagePopup";
import AddData from "./AddData";
import { Box } from "@mui/material";
import ViewData from "./ViewData";
import BulkUploadModal1 from "../../../Components/Common/BulkUploadModal1";
import { formatNumber } from "../../../utils/formatNumber";
import { formatNumberCommaSeparated } from "../../../utils/common";
import BulkUpload from "./BulkUpload";
import { useUser } from "../../../context/AuthContext";
import {
  shouldShowFacilityColumn,
  withFacilityViewTableHeader,
  mapTableDataWithFacility,
} from "../../../utils/facilityColumn";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";
import BulkUploadSkippedDialog from "../../../Components/Common/BulkUploadSkippedDialog";
import { downloadWasteGeneratedTemplateExcel } from "../../../utils/wasteGeneratedExcelTemplate";

/** Allowed waste management methods per waste type (AddData + Excel / bulk). */
const WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE = {
  Aggregates: ["Recycling", "Landfilling"],
  "Average construction": ["Recycling", "Incineration"],
  Asbestos: ["Landfilling"],
  Asphalt: ["Recycling", "Landfilling"],
  Bricks: ["Recycling", "Landfilling"],
  Concrete: ["Recycling", "Landfilling"],
  Insulation: ["Recycling", "Landfilling"],
  Metals: ["Recycling", "Landfilling"],
  Soils: ["Recycling", "Landfilling"],
  "Mineral oil": ["Recycling", "Incineration"],
  Plasterboard: ["Recycling", "Landfilling"],
  Tyres: ["Recycling"],
  Wood: ["Recycling", "Incineration", "Landfilling"],
  Books: ["Recycling", "Incineration", "Landfilling"],
  Glass: ["Recycling", "Incineration", "Landfilling"],
  Clothing: ["Recycling", "Incineration", "Landfilling"],
  "Organic mixed food and garden waste": [
    "Incineration",
    "Landfilling",
    "Composting",
  ],
  "Electrical item": ["Recycling", "Incineration", "Landfilling"],
  Plastic: ["Recycling", "Incineration", "Landfilling"],
  "Paper and board": ["Recycling", "Incineration", "Landfilling"],
  Sludge: ["Recycling", "Landfilling", "Incineration"],
  Effluent: ["Recycling"],
  "ETP Sludge": ["Recycling", "Incineration", "Landfilling"],
  "STP Sludge": ["Landfilling", "Incineration", "Composting"],
  Sewage: ["Recycling"],
};

const WASTE_GENERATED_SKIPPED_DOWNLOAD_COLUMNS = [
  "Month (in 01/25 or Jan-25)",
  "Waste Category",
  "Waste Type",
  "Waste Management Method",
  "Vendor Name",
  "Quantity of Waste Generated (Tonnes, or Cubic Metres for Wastewater)",
  "Density(in Case of Waste Type is Effluent,Sewage)",
  "Quantity of Waste Managed",
];

const WasteGenerated = () => {
  const { user } = useUser();
  const showFacilityColumn = shouldShowFacilityColumn(user);
  const [cardData, setCardData] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [filters, setFilters] = useState({
    facility: [],
    wasteType: [],
    wasteManagementMethod: [],
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [isLoading, setIsLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState(1);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletedItemId, setDeletedItemId] = useState("");
  const [selectedWasteMethod, setSelectedWasteMethod] = useState("");
  /** Latest cardData request wins (avoids empty `wasteMethod` response overwriting Recycling, etc.). */
  const cardDataFetchIdRef = useRef(0);
  const tableContainerRef = useRef(null);
  const [isShortContent, setIsShortContent] = useState(false);
  const [matchData, setMatchData] = useState([]);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState({});
  const [jsonBulkUpload, setJsonBulkUpload] = useState([]);
  const [excelData, setExcelData] = useState(null);
  const [skippedUploadDialog, setSkippedUploadDialog] = useState({
    open: false,
    skippedEntries: [],
    inserted: 0,
    skipped: 0,
  });

  const uploadErrorClose = () => {
    setBulkUploadTable(true);
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight;
      const screenHeight = window.innerHeight;
      setIsShortContent(tableHeight < screenHeight / 3.2);
    }
  }, [tableData]);

  const wasteMethodForCard =
    selectedWasteMethod || filters?.wasteManagementMethod?.[0] || "";

  const fetchCardData = async (wasteMethod) => {
    const method = String(wasteMethod ?? "").trim();
    if (!method) {
      return;
    }
    const fetchId = ++cardDataFetchIdRef.current;
    try {
      const response = await get(
        `${api}/scope3/wasteGeneratedInOperations/cardData`,
        { params: { wasteMethod: method } }
      );
      if (fetchId !== cardDataFetchIdRef.current) {
        return;
      }
      setCardData(response.data.data);
    } catch (error) {
      console.error("Error fetching card data:", error);
    }
  };

  /** Waste types allowed per waste category (drawer + validation in AddData). */
  const wasteTypesByCategory = {
    "Hazardous Waste": [ 
      "Asbestos",
      "Mineral oil",
      "Electrical item",
      "ETP Sludge",
      "Effluent"
    ],
    "Non-Hazardous Waste": [
      "Metals",
      "Tyres",
      "Wood",
      "Books",
      "Glass",
      "Clothing",
      "Organic mixed food and garden waste",
      "Plastic",
      "Paper and board",
      "Sewage",
      "STP Sludge"
    ],
    "Construction & Demolition Waste": [
      "Aggregates",
      "Average construction",
      "Asphalt",
      "Bricks",
      "Concrete",
      "Insulation",
      "Soils",
      "Plasterboard"

    ],
   
  };

  /** Union of all types (Excel template rows, bulk hints). Order: categories then extras. */
  const wasteTypeOptions = [
    ...new Set(Object.values(wasteTypesByCategory).flat()),
  ];
  const bannerData = [
    {
      title: "Total Quantity of Waste Generated",
      description: formatNumber(cardData?.totalWasteGenerated || 0),
      unit: "Tonnes",
    },
    {
      title: "Top Waste Types",
      description: (cardData?.topWasteTypes ?? []).map((el) => el._id),
    },
    {
      title: "Total Quantity of Waste Managed",
      description:
        formatNumberCommaSeparated(
          formatNumber(
            cardData?.wasteManagedByMethod?.totalManagedQuantity || 0
          )
        ) + " Tonnes",
      filter: "option",
      key: "wasteManagedByMethod",
      filterValues: filters?.wasteManagementMethod ?? [],
      defaultValue: selectedWasteMethod,
    },
  ];

  const wasteCategoryOptions = [
    "Hazardous Waste",
    "Non-Hazardous Waste",
    "Construction & Demolition Waste",
  ];

  const fetchExcelData = async () => {
    await get("/scope3/wasteGeneratedInOperations/generateWasteGeneratedExcel")
      .then((res) => {
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
  
        // Create Method => Vendors mapping
        const vendorsByMethod = {};
  
        data.forEach((vendor) => {
          const vendorName = vendor?.vendorLegalName || "";
  
          const methods = Array.isArray(vendor?.wasteManagementType)
            ? vendor.wasteManagementType
            : vendor?.wasteManagementType
            ? [vendor.wasteManagementType]
            : [];
  
          methods.forEach((method) => {
            if (!vendorsByMethod[method]) {
              vendorsByMethod[method] = [];
            }
  
            if (!vendorsByMethod[method].includes(vendorName)) {
              vendorsByMethod[method].push(vendorName);
            }
          });
        });

  
        const maxRows = Math.max(
          wasteCategoryOptions.length,
          wasteTypeOptions.length
        );
  
        const optionsRows = [];
  
        let lastCategoryForDedupe = null;
  
        for (let i = 0; i < maxRows; i++) {
          const wasteType = wasteTypeOptions[i] || "";
  
          const methodsForType =
            wasteType && WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE[wasteType]
              ? WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE[wasteType]
              : [];
  
          const categoryCell = wasteCategoryOptions[i] || "";
  
          if (!methodsForType.length) {
            const categoryDisplay =
              categoryCell === lastCategoryForDedupe ? "" : categoryCell;
  
            lastCategoryForDedupe = categoryCell;
  
            optionsRows.push({
              "Month (in 01/25 or Jan-25)": "",
              "Waste Category": categoryDisplay,
              "Waste Type": wasteType,
              "Waste Management Method": "",
              "Vendor Name": "",
              "Quantity of Waste Generated (Tonnes, or Cubic Metres for Wastewater)":
                "",
              "Density(in Case of Waste Type is Effluent,Sewage)": "",
              "Quantity of Waste Managed": "",
            });
  
            continue;
          }
  
          methodsForType.forEach((method, methodIndex) => {
            const vendors = vendorsByMethod[method] || [];
  
            if (!vendors.length) {
              optionsRows.push({
                "Month (in 01/25 or Jan-25)": "",
                "Waste Category":
                  methodIndex === 0 &&
                  categoryCell !== lastCategoryForDedupe
                    ? categoryCell
                    : "",
                "Waste Type": methodIndex === 0 ? wasteType : "",
                "Waste Management Method": method,
                "Vendor Name": "",
                "Quantity of Waste Generated (Tonnes, or Cubic Metres for Wastewater)":
                  "",
                "Density(in Case of Waste Type is Effluent,Sewage)": "",
                "Quantity of Waste Managed": "",
              });
  
              lastCategoryForDedupe = categoryCell;
              return;
            }
  
            vendors.forEach((vendorName, vendorIndex) => {
              optionsRows.push({
                "Month (in 01/25 or Jan-25)": "",
                "Waste Category":
                  methodIndex === 0 &&
                  vendorIndex === 0 &&
                  categoryCell !== lastCategoryForDedupe
                    ? categoryCell
                    : "",
                "Waste Type":
                  methodIndex === 0 && vendorIndex === 0
                    ? wasteType
                    : "",
                "Waste Management Method":
                  vendorIndex === 0 ? method : "",
                "Vendor Name": vendorName,
                "Quantity of Waste Generated (Tonnes, or Cubic Metres for Wastewater)":
                  "",
                "Density(in Case of Waste Type is Effluent,Sewage)": "",
                "Quantity of Waste Managed": "",
              });
            });
  
            lastCategoryForDedupe = categoryCell;
          });
        }
  
        optionsRows.push({
          "Month (in 01/25 or Jan-25)": "",
          "Waste Category": "",
          "Waste Type(in Case of WasteWater WasteType should be Sludge or Effluent)":
            "",
          "Waste Management Method": "",
          "Vendor Name": "",
          "Quantity of Waste Generated (Tonnes, or Cubic Metres for Wastewater)":
            "",
          "Density(in Case of Waste Type is Effluent,Sewage)": "",
          "Quantity of Waste Managed": "",
        });
  
        setExcelData(optionsRows);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchExcelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; options are stable module-level shapes
  }, []);

  const inputData = [
    {
      fieldName: "consumptionPeriod",
      title: "Waste Management Month",
      required: true,
      type: "date",
      width: "2rem",
    },
    {
      fieldName: "wasteCategory",
      title: "Waste Category",
      type: "select",
      options: wasteCategoryOptions,
      width: "5rem",
    },
    {
      fieldName: "wasteType",
      title: "Waste Type",
      type: "select",
      /** Resolved in AddData from `wasteTypesByCategory` + selected `wasteCategory`. */
      options: [],
    },

    {
      fieldName: "quantityOfWasteGenerated",
      title: "Quantity of Waste Generated",
      placeholder: "Enter Quantity",
      type: "number",
      nested: true,
      unitOptions: ["Tonnes", "Cubic Meter"],
    },
    {
      fieldName: "wasteManagementMethod",
      title: "Waste Management Method",
      placeholder: "Select Waste Management Method",
      type: "select",
      /** Options come from `wasteManagementMethodsByWasteType` for the selected waste type (AddData). */
      options: [],
    },
    {
      fieldName: "vendorName",
      title: "Vendor Name of Waste Management",
      placeholder: "Select Vendor",
      type: "select",
      url: `${api}/master/wasteManagement`,
      field: "vendorLegalName",
      // Assuming you have a list of vendors from some API
    },
    {
      fieldName: "density",
      title: "Density",
      placeholder: "Enter Density",
      type: "number",
    },
    {
      fieldName: "quantityOfWasteManaged",
      title: "Quantity of Waste Managed",
      placeholder: "Enter Quantity",
      type: "number",
      nested: true,
      // options: ["Kg", "Tons"],
    },
    {
      fieldName: "editRemarkReason",
      title: "Edit Remark Reason",
      placeholder: "Additional edit remarks",
      type: "text",
      condition: true,
    },
    {
      fieldName: "documents",
      title: "Upload Documents (optional)",
      placeholder: "Upload Documents",
      type: "file", // assuming you allow uploading multiple files
    },
  ];

  /** Column widths mirror Air Transportation `ViewTable` (fixed % + default action column). */
  const headers = withFacilityViewTableHeader([
    { key: "createdAt", label: "Date", sortable: true, width: "12%" },
    {
      key: "consumptionPeriod",
      label: "Waste Management Month",
      sortable: true,
      width: "15%",
    },
    { key: "wasteType", label: "Waste Type", sortable: true, width: "15%" },
    {
      key: "quantityOfWasteGenerated",
      label: "Quantity of Waste Generated",
      sortable: true,
      width: "17%",
    },
    {
      key: "wasteManagementMethod",
      label: "Waste Management Method",
      sortable: true,
      width: "16%",
    },
    {
      key: "quantityOfWasteManaged",
      label: "Quantity of Waste Managed",
      sortable: true,
      width: "15%",
    },
  ], showFacilityColumn);

  const filterData = [
    { title: "Date", type: "date", key: "createdAt", value: "" },
    {
      key: "consumptionMonth",
      type: "month",
      title: "Waste Mangement Month",
      sortable: true,
    },
    {
      title: "Waste Type",
      type: "option",
      key: "wasteType",
      value: filters?.wasteType,
    },
    {
      title: "Quantity of Waste Generated",
      type: "limit",
      key: "quantity",
      value: [],
      min: "minQty",
      max: "maxQty",
    },
    {
      title: "Waste Management Method",
      type: "option",
      key: "wasteManagementMethod",
      value: filters?.wasteManagementMethod,
    },
    {
      title: "Quantity of Waste Managed",
      type: "limit",
      key: "quantityManaged",
      value: [],
      min: "minQtyManaged",
      max: "maxQtyManaged",
    },
  ];

  const updatePage = () => {
    if (page > 1 && tableData?.length === 1) {
      setPage(page - 1);
    }
    setIsLoading(true);
    fetchData();
  };

  /**
   * Refreshes table + banner cards. Pass `savedWasteManagementMethod` after add/edit so
   * cardData uses the same method as the new row (dropdown may still be empty / other method).
   */
  const fetchData = async (savedWasteManagementMethod) => {
    let filter = { ...filterObj };
    const override =
      savedWasteManagementMethod != null &&
      String(savedWasteManagementMethod).trim() !== ""
        ? String(savedWasteManagementMethod).trim()
        : "";
    if (override) {
      setSelectedWasteMethod((prev) => (prev === override ? prev : override));
      /** Ensure dropdown options include the method just saved (API filter list can lag; blank Select if value ∉ MenuItem). */
      setFilters((f) => {
        const existing = f?.wasteManagementMethod ?? [];
        if (existing.map(String).includes(override)) return f;
        return {
          ...f,
          wasteManagementMethod: [...existing, override],
        };
      });
    }
    const methodForCard = override || wasteMethodForCard;
    await fetchCardData(methodForCard);
    if (filter.quantity) {
      filter.minQty = parseInt(filterObj.quantity.min);
      filter.maxQty = parseInt(filterObj.quantity.max);
      delete filter.quantity;
    }
    if (filter.quantityManaged) {
      filter.minQtyManaged = parseInt(filterObj.quantityManaged.min);
      filter.maxQtyManaged = parseInt(filterObj.quantityManaged.max);
      delete filter.quantityManaged;
    }
    if (filter.createdAt) {
      filter.startDate = filter?.createdAt?.start || "";
      filter.endDate = filter?.createdAt?.end || "";
      delete filter?.createdAt;
    }
    if (filter.consumptionMonth) {
      filter.startMonth = filter?.consumptionMonth?.start || "";
      filter.endMonth = filter?.consumptionMonth?.end || "";
      delete filter?.consumptionMonth;
    }

    // List GET is not scoped by banner card method — show all rows unless the filter modal sets wasteManagementMethod.

    const queryParams = new URLSearchParams({
      ...filter,
      page: currentPage,
      limit: rowsPerPage,
      sortField: sortConfig.key,
      sortOrder: sortConfig.direction,
    }).toString();
    try {
      const res = await get(
        `/scope3/wasteGeneratedInOperations/get?${queryParams}`
      );
      setTotalPages(res.data.totalPages);
      setTableData(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchData();
    /** Intentionally omit `selectedWasteMethod`: dropdown changes call `fetchData(method)` directly; including it caused a second concurrent fetch and races. */
  }, [sortConfig, currentPage, page, filterObj, filters]);

  const fetchFilters = async () => {
    try {
      const { data } = await get(`/scope3/wasteGeneratedInOperations/filter`);
      const { wasteType, wasteManagementMethod } = data?.data || {};
      const methodList = wasteManagementMethod || [];
      setFilters({
        wasteType: wasteType || [],
        wasteManagementMethod: methodList,
      });

      setSelectedWasteMethod((prev) => {
        const first = methodList[0] != null ? String(methodList[0]).trim() : "";
        if (prev != null && String(prev).trim() !== "") {
          const prevStr = String(prev).trim();
          if (methodList.map(String).includes(prevStr)) {
            return prevStr;
          }
        }
        return first;
      });
    } catch (err) {
      console.error("Fetch Filters Error:", err);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
    } else if (type === "edit") {
      setEditModal(true);
      setEditData(dataForEdit);
      setViewModal(false);
    }
  };

  const handleEdit = () => {
    // data.createdAt = data?.createdAt.split("T")[0];
    // data.dateOfTravel = data?.dateOfTravel.split("T")[0];
    setEditData(editData);
    setViewModal(false);
    setEditModal(true);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };


  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSelectChange = (key, value, filterType) => {
    setCurrentPage(1);
    const method = value != null ? String(value).trim() : "";
    if (!method) return;
    /** Drive card API + table immediately; `fetchData` also syncs `selectedWasteMethod` when given a method. */
    if (key === "wasteManagedByMethod") {
      void fetchData(method);
    } else {
      setSelectedWasteMethod(method);
    }
  };

  const handleDeleteOpen = (itemId) => {
    setDeletedItemId(itemId);
    setDeleteDialog(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    fetchData();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        backgroundColor: "#ededed",
      }}
    >
      <Cards
        width="7rem"
        data={bannerData}
        title={"Waste Generated in Operations"}
        onFilterChange={handleSelectChange}
      />
      <MessagePopup {...{ snackOpen, setsnackOpen }} />{" "}
      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />
      <ViewTable
        tableContainerRef={tableContainerRef}
        isShortContent={isShortContent}
        title={"Waste Generated in Operations"}
        headers={headers}
        data={mapTableDataWithFacility(tableData, showFacilityColumn)}
        onSort={handleSort}
        sortConfig={sortConfig}
        editIcon={user?.companyAdmin ? "disable" : "enable"}
        totalPages={totalPages}
        page={currentPage}
        onPageChange={handlePageChange}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row._id)}
      />
      <AddData
        handleClose={() => closeModal(editModal ? "edit" : "add")}
        id={editData ? editData._id : ""}
        title="Waste Generated in Operations"
        addData={editModal ? editModal : isModalOpen}
        addUrl={`${api}/scope3/wasteGeneratedInOperations/add`}
        updateUrl={`${api}/scope3/wasteGeneratedInOperations/update`}
        {...{
          fetchData,
          setsnackOpen,
          inputData,
          wasteTypesByCategory,
          wasteManagementMethodsByWasteType: WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE,
          element: editData,
          setAddData: setEditModal,
        }}
      />
      <ViewData
        id={editData ? editData._id : ""}
        element={editData}
        title={"Waste Generated in Operation Details"}
        viewData={viewModal}
        setData={setEditData}
        setViewData={setViewModal}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={user?.companyAdmin}
      />
      {deleteDialog && (
        <DeleteAlertDialog
          fetchData={updatePage}
          {...{
            deleteDialog,
            setDeleteDialog,
            id: deletedItemId,
            url: `${api}/scope3/wasteGeneratedInOperations/delete`,
            setsnackOpen,
          }}
        />
      )}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />
      {bulkUploadModal && (
        <BulkUploadModal1
          onCloseBulkUpload={handleCloseBulkUpload}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          setMatchData={setMatchData}
          formDataBulkUpload={formDataBulkUpload}
          setFormDataBulkUpload={setFormDataBulkUpload}
          setJsonBulkUpload={setJsonBulkUpload}
          matchedKey="vendorStatusList"
          errorClose={uploadErrorClose}
          setBulkUploadModal={setBulkUploadModal}
          fileName={"Waste Generated"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/wasteGeneratedInOperations/bulkupload"}
          get={get}
        />
      )}
      {bulkUploadTable && (
        <BulkUpload
          matchData={matchData}
          inputData={inputData.slice(0, -3)}
          setMatchData={setMatchData}
          onClose={() => setBulkUploadTable(false)}
          bulkUploadTable={bulkUploadTable}
          {...{ formDataBulkUpload, setFormDataBulkUpload }}
          jsonBulkUpload={jsonBulkUpload}
          bulkUploadUrl={`${api}/scope3/wasteGeneratedInOperations/jsonupload`}
          setsnackOpen={setsnackOpen}
          title1="Vendor Name"
          title2="Waste Type"
          title3="Waste Management Method"
          keyValue1="vendor"
          keyValue2="wasteType"
          keyValue3="wasteManagementMethod"
          matched1={"vendorMatched"}
          matched2={"wasteTypeMatched"}
          matched3={"wasteManagementMethodMatched"}
          options1={["categoryData"]}
          options2={wasteTypeOptions}     
          options3={WASTE_MANAGEMENT_METHODS_BY_WASTE_TYPE}
          onConfirm={(data) => {
            setBulkUploadTable(false);
            fetchData();

            const skippedEntries = data?.skippedEntries;
            const hasSkipped =
              Array.isArray(skippedEntries) && skippedEntries.length > 0;
            const inserted = Number(data?.inserted ?? 0);
            const skipped = Number(data?.skipped ?? 0);

            if (hasSkipped) {
              setSkippedUploadDialog({
                open: true,
                skippedEntries,
                inserted,
                skipped,
              });
            }
          }}
        />
      )}
      <BulkUploadSkippedDialog
        open={skippedUploadDialog.open}
        skippedEntries={skippedUploadDialog.skippedEntries}
        inserted={skippedUploadDialog.inserted}
        skipped={skippedUploadDialog.skipped}
        downloadFileName="waste_generated_skipped"
        downloadExcelColumns={WASTE_GENERATED_SKIPPED_DOWNLOAD_COLUMNS}
        downloadHandler={(rows) =>
          downloadWasteGeneratedTemplateExcel({
            get,
            fileName: "waste_generated_skipped",
            rows,
          })
        }
        onClose={() =>
          setSkippedUploadDialog({
            open: false,
            skippedEntries: [],
            inserted: 0,
            skipped: 0,
          })
        }
      />
    </Box>
  );
};

export default WasteGenerated;
